import PropTypes from 'prop-types';
import React, { Activity, useCallback, useState } from 'react';
import { CHAT_INPUT_MODES, ChatAPI, states } from '@guuru/chat-web';
import { emailHelper, textHelper } from '@guuru/helpers-common';
import useCannedResponses from './hooks/useCannedResponses';
import useHideMessageBar from './hooks/useHideMessageBar';
import useUpdateInputBar from './hooks/useUpdateInputBar';
import useUserIsTyping from './hooks/useUserIsTyping';
import MessageBar from './MessageBar';
import ReviewRateChatInput from './ReviewRateChatInput';
import StartNewFreshChatInput from './StartNewFreshChatInput';
import TextInput from './TextInput';

const { STATE_DISABLED, STATE_ENABLED } = states;

const ChatMessageBar = function ({
  chatId = '',
  isUser = false,
  isClosed = false,
  isReviewingRate = false,
  isFinished = false,
  inputMode = null,
  onSubmit,
  onAskNewQuestion = () => {},
  onInputFocus = () => {},
  hasTextAction = null,
  onAttachmentSubmit = () => {
    throw new Error('Missing onAttachmentSubmit');
  },
  partnerId = '',
  inputState = '',
  noFocus = false,
}) {
  const [text, setText] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isEnabled, setIsEnabled] = useState(STATE_DISABLED);
  const { initIsTyping, stopIsTyping } = useUserIsTyping(chatId, isUser);
  const hideMessageBar = useHideMessageBar(
    isUser,
    isFinished,
    partnerId,
    inputMode,
  );
  const [handleCannedResponseInput, cannedResponses] = useCannedResponses(
    partnerId,
    { skip: isUser },
  );

  /** Handler for input text change */
  const handleInput = useCallback(
    (value, onChange) => {
      switch (inputMode) {
        case CHAT_INPUT_MODES.email: {
          const isValidValue = emailHelper.isValid(value);
          setText(value);
          setIsValid(isValidValue);
          setIsEnabled(isValidValue ? STATE_ENABLED : STATE_DISABLED);
          break;
        }
        case CHAT_INPUT_MODES.chat:
          initIsTyping();
        // eslint-disable-next-line no-fallthrough
        default:
          if (chatId) {
            ChatAPI.storeChatUserPendingInput(chatId, value);
          }
          setText(value);
          setIsValid(true);
          setIsEnabled(
            value.trim().length > 0 ? STATE_ENABLED : STATE_DISABLED,
          );

          if (onChange) handleCannedResponseInput(value);
      }
    },
    [inputMode, initIsTyping, handleCannedResponseInput, chatId],
  );

  useUpdateInputBar(chatId, inputMode, text, handleInput);

  const submit = useCallback(
    (eventLabel, newText) => {
      const textType = textHelper.validateSingleEmoji(newText);

      if (!isValid || isEnabled !== STATE_ENABLED) return;
      if (newText.length <= 0) return;
      if (!isUser && newText.startsWith('/')) return;
      if (!onSubmit) return;

      const isMessageBarLoading = inputMode === CHAT_INPUT_MODES.loading;
      const isChatReady = inputMode === CHAT_INPUT_MODES.chat && !chatId;

      if (isUser && (isMessageBarLoading || isChatReady)) return;
      const message = isUser ? newText.replaceAll(/\n./g, '') : newText;
      onSubmit(eventLabel, message, textType);
      stopIsTyping();
      setText('');
      if (chatId) {
        ChatAPI.deleteChatUserPendingInput(chatId);
      }
      setIsEnabled(STATE_DISABLED);
    },
    [chatId, inputMode, isEnabled, isUser, isValid, onSubmit, stopIsTyping],
  );

  if (hideMessageBar) {
    return null;
  }

  const showTextInput =
    !isUser || (!isClosed && !isFinished && !isReviewingRate);
  const showReviewRateInput = isUser && isReviewingRate;
  const showStartNewChatInput = isUser && isFinished;

  return (
    <MessageBar>
      <Activity mode={showTextInput ? 'visible' : 'hidden'}>
        <TextInput
          chatId={chatId}
          isVisible={showTextInput}
          isEnabled={isEnabled === STATE_ENABLED}
          isValid={isValid}
          isUser={isUser}
          text={text}
          noFocus={noFocus}
          partnerId={partnerId}
          onAttachmentSubmit={onAttachmentSubmit}
          hasTextAction={hasTextAction}
          inputMode={inputMode}
          cannedResponses={cannedResponses}
          inputState={inputState}
          handleInputFocus={onInputFocus}
          handleInput={handleInput}
          submit={submit}
        />
      </Activity>
      <Activity mode={showReviewRateInput ? 'visible' : 'hidden'}>
        <ReviewRateChatInput chatId={chatId} />
      </Activity>
      <Activity mode={showStartNewChatInput ? 'visible' : 'hidden'}>
        <StartNewFreshChatInput onAskNewQuestion={onAskNewQuestion} />
      </Activity>
    </MessageBar>
  );
};

ChatMessageBar.propTypes = {
  inputMode: PropTypes.string,
  chatId: PropTypes.string,
  partnerId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  isUser: PropTypes.bool,
  onAskNewQuestion: PropTypes.func,
  onInputFocus: PropTypes.func,
  onAttachmentSubmit: PropTypes.func,
  isClosed: PropTypes.bool,
  isReviewingRate: PropTypes.bool,
  isFinished: PropTypes.bool,
  inputState: PropTypes.string,
  hasTextAction: PropTypes.func,
  noFocus: PropTypes.bool,
};

export default ChatMessageBar;
